import { Tool, Web3DViewer } from '@technology/web3d';
import {
  isToolInteractionPaneForWrapper,
  Web3DViewerWrapper,
} from '@trimble/web3d-utils';
import { Accessor, createEffect, createRoot, untrack } from 'solid-js';

const SHAPE_SETTLED_EVENTS = ['markupmodified', 'markupremoved'] as const;

// web3d markup tools only expose creation progress through their protected `_finished` getter.
function hasShapeInProgress(tool: Tool): boolean {
  return '_finished' in tool && tool._finished === false;
}

/**
 * Keeps a tool that a capability turned on switched off while its viewer is not the
 * tool interaction pane, unless a shape started on that viewer is still unfinished.
 * Every enabled snapping tool picks on each window mouse move, wherever the pointer is.
 */
export class PaneToolGate {
  private readonly requested = new Map<Tool, Web3DViewer>();
  private readonly heldOff = new Set<Tool>();
  private readonly settledListeners = new Map<Web3DViewer, EventListener>();
  private readonly disposeEffect: () => void;

  constructor(
    private readonly viewerWrappers: Accessor<Web3DViewerWrapper[]>,
    private readonly canResume: (viewer: Web3DViewer, tool: Tool) => boolean
  ) {
    this.disposeEffect = createRoot((dispose) => {
      createEffect(() => {
        for (const wrapper of this.viewerWrappers()) {
          isToolInteractionPaneForWrapper(wrapper);
        }
        untrack(() => {
          for (const tool of [...this.requested.keys()]) {
            this.apply(tool);
          }
        });
      });
      return dispose;
    });
  }

  /** Call right after the capability turned `tool` on. */
  public request(viewer: Web3DViewer, tool: Tool): void {
    this.requested.set(tool, viewer);
    this.heldOff.delete(tool);
    this.listenForSettledShapes(viewer);
    // Callers often run inside their own effects, which must not re-run (and restart
    // the tool) whenever the interaction pane changes.
    untrack(() => this.apply(tool));
  }

  /** Stops gating `tool` and turns it back on if the gate was holding it off and it may resume. */
  public release(tool: Tool): void {
    const viewer = this.requested.get(tool);
    this.requested.delete(tool);
    if (this.heldOff.delete(tool) && viewer && this.canResume(viewer, tool)) {
      tool.enabled = true;
    }
    if (viewer && ![...this.requested.values()].includes(viewer)) {
      this.stopListening(viewer);
    }
  }

  public dispose(): void {
    this.disposeEffect();
    for (const viewer of [...this.settledListeners.keys()]) {
      this.stopListening(viewer);
    }
    this.requested.clear();
    this.heldOff.clear();
  }

  private apply(tool: Tool): void {
    const viewer = this.requested.get(tool);
    if (!viewer) {
      return;
    }
    const open = this.isInteractionPane(viewer) || hasShapeInProgress(tool);
    if (open && this.heldOff.has(tool)) {
      this.heldOff.delete(tool);
      if (this.canResume(viewer, tool)) {
        tool.enabled = true;
      } else {
        this.requested.delete(tool);
      }
    } else if (!open && !this.heldOff.has(tool) && tool.enabled) {
      this.heldOff.add(tool);
      tool.enabled = false;
    }
  }

  private isInteractionPane(viewer: Web3DViewer): boolean {
    const wrapper = this.viewerWrappers().find((w) => w.viewer === viewer);
    return wrapper ? isToolInteractionPaneForWrapper(wrapper) : true;
  }

  private listenForSettledShapes(viewer: Web3DViewer): void {
    if (this.settledListeners.has(viewer)) {
      return;
    }
    const listener = (): void => {
      // The tool marks the shape finished around the event, so read it afterwards.
      queueMicrotask(() => {
        for (const [tool, toolViewer] of [...this.requested]) {
          if (toolViewer === viewer) {
            this.apply(tool);
          }
        }
      });
    };
    for (const event of SHAPE_SETTLED_EVENTS) {
      viewer.addEventListener(event, listener);
    }
    this.settledListeners.set(viewer, listener);
  }

  private stopListening(viewer: Web3DViewer): void {
    const listener = this.settledListeners.get(viewer);
    if (!listener) {
      return;
    }
    for (const event of SHAPE_SETTLED_EVENTS) {
      viewer.removeEventListener(event, listener);
    }
    this.settledListeners.delete(viewer);
  }
}
